import { BaseComponent } from '../core/BaseComponent.js';
import { showToast } from '../utils/toast.js';

export class CaseManager extends BaseComponent {
    constructor(selector = '#add_case_manager_modal', options = {}) {
        super(selector, options);

        this.openButtonSelector = '#add_case_manager';
        this.closeButtonSelector = '.close_modal, .cancel_button';
        this.formSelector = '#add-case-manager-form';
        this.tableBodySelector = '#case_managers tbody';

        this.apiAction = 'submit_new_company_member_submission';

        // Delete modal
        this.deleteModal = null;
        this.deletingUserId = null;
    }

    init() {
        if (!super.init()) return this;

        this.cacheElements();
        this.bindEvents();
        this.initDeleteModal();
        this.initEditModal();

        return this;
    }

    cacheElements() {
        this.form = this.$(this.formSelector);
        this.closeButtons = this.$$(this.closeButtonSelector);
        this.openButton = document.querySelector(this.openButtonSelector);
        this.tableBody = document.querySelector(this.tableBodySelector);
    }

    bindEvents() {
        if (!this.form || !this.openButton) return;

        this.openButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.open();
        });

        this.closeButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.close();
            });
        });

        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // ── Add modal ────────────────────────────────────────────────────────────

    open() {
        this.fadeIn(this.element);
    }

    close() {
        this.fadeOut(this.element);
    }

    async handleSubmit(e) {
        e.preventDefault();

        this.setLoading(true);

        try {
            const formData = new FormData(this.form);
            const params = new URLSearchParams(formData);

            params.append('action', this.apiAction);
            params.append('nonce', docuswiftPortal.nonce);
            params.append('form_data', params.toString());

            const response = await fetch(docuswiftPortal.ajax_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: params.toString()
            });

            const result = await response.json();

            if (result.success) {
                this.appendMember(result.data.new_table_row);
                this.form.reset();
                this.close();
                this.toast(result.data.message || 'Case manager added.', 'success');
            } else {
                this.toast(result.data.message || 'Error.', 'error');
            }

        } catch (error) {
            console.error('AJAX Error:', error);
            this.toast('Network error. Please try again.', 'error');
        } finally {
            this.setLoading(false);
        }
    }

    appendMember(html) {
        if (!this.tableBody) return;
        this.tableBody.insertAdjacentHTML('beforeend', html);
    }

    setLoading(isLoading) {
        const btn = this.form?.querySelector('.invite_button');
        if (!btn) return;
        if (isLoading) {
            btn._originalText = btn.textContent;
            btn.disabled = true;
            btn.innerHTML = '<span class="btn-spinner"></span>';
        } else {
            btn.disabled = false;
            btn.textContent = btn._originalText || 'Send Invite';
        }
    }

    // ── Delete modal ─────────────────────────────────────────────────────────

    initDeleteModal() {
        this.deleteModal = document.getElementById('delete_member_modal');
        if (!this.deleteModal) return;

        // Open on trash icon click (delegated – works for dynamically added rows too)
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.delete-member-btn');
            if (!btn) return;
            e.preventDefault();
            this.openDeleteModal(btn.dataset.userId, btn.dataset.userName);
        });

        // Close buttons
        this.deleteModal.querySelector('.close_modal')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeDeleteModal();
        });
        document.getElementById('delete-member-cancel')?.addEventListener('click', () => {
            this.closeDeleteModal();
        });
        this.deleteModal.querySelector('.overlay')?.addEventListener('click', () => {
            this.closeDeleteModal();
        });

        // Confirm delete
        document.getElementById('delete-member-confirm')?.addEventListener('click', () => {
            this.confirmDelete();
        });
    }

    async openDeleteModal(userId, userName) {
        if (!this.deleteModal) return;

        this.deletingUserId = userId;

        // Reset modal state
        document.getElementById('delete-member-name').textContent = userName || 'this user';
        document.getElementById('delete-member-user-id').value = userId;
        document.getElementById('delete-member-transfer-wrap').style.display = 'none';
        document.getElementById('delete-member-loading').style.display = 'flex';
        this.resetDeleteBtn(true); // disabled while loading

        this.fadeIn(this.deleteModal);

        try {
            const params = new URLSearchParams({
                action: 'get_delete_member_info',
                user_id: userId,
                nonce: docuswiftPortal.nonce,
            });

            const res  = await fetch(docuswiftPortal.ajax_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: params.toString(),
            });
            const data = await res.json();

            document.getElementById('delete-member-loading').style.display = 'none';

            if (!data.success) {
                this.toast(data.data?.message || 'Error loading member info.', 'error');
                this.closeDeleteModal();
                return;
            }

            const { submissions_count, other_members } = data.data;

            if (submissions_count > 0) {
                document.getElementById('delete-member-submissions-count').textContent = submissions_count;
                const select = document.getElementById('delete-member-transfer-to');
                select.innerHTML = '<option value="">— Select case manager —</option>';
                other_members.forEach(m => {
                    const opt = document.createElement('option');
                    opt.value = m.id;
                    opt.textContent = m.name;
                    select.appendChild(opt);
                });
                document.getElementById('delete-member-transfer-wrap').style.display = 'block';
            }

            document.getElementById('delete-member-confirm').disabled = false;

        } catch (err) {
            console.error(err);
            document.getElementById('delete-member-loading').style.display = 'none';
            this.toast('Network error. Please try again.', 'error');
            this.closeDeleteModal();
        }
    }

    closeDeleteModal() {
        this.fadeOut(this.deleteModal);
        this.deletingUserId = null;
    }

    async confirmDelete() {
        const userId     = document.getElementById('delete-member-user-id').value;
        const transferTo = document.getElementById('delete-member-transfer-to')?.value || '';
        const nonce      = document.querySelector('#delete_member_modal [name="delete_company_member_nonce"]')?.value || '';

        const transferWrap = document.getElementById('delete-member-transfer-wrap');
        if (transferWrap.style.display !== 'none' && !transferTo) {
            this.toast('Please select a case manager to transfer data to.', 'error');
            return;
        }

        const confirmBtn = document.getElementById('delete-member-confirm');
        confirmBtn.disabled = true;
        confirmBtn.innerHTML = '<span class="btn-spinner"></span>';

        try {
            const params = new URLSearchParams({
                action:      'delete_company_member',
                user_id:     userId,
                transfer_to: transferTo,
                nonce:       nonce,
            });

            const res  = await fetch(docuswiftPortal.ajax_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: params.toString(),
            });
            const data = await res.json();

            if (data.success) {
                const row = this.tableBody?.querySelector(`[data-user-id="${userId}"]`)?.closest('tr');
                row?.remove();
                this.resetDeleteBtn();
                this.closeDeleteModal();
                this.toast(data.data.message || 'Case manager deleted.', 'success');
            } else {
                this.toast(data.data?.message || 'Failed to delete user.', 'error');
                this.resetDeleteBtn(false);
            }

        } catch (err) {
            console.error(err);
            this.toast('Network error. Please try again.', 'error');
            this.resetDeleteBtn(false);
        }
    }

    // ── Edit modal ───────────────────────────────────────────────────────────

    initEditModal() {
        this.editModal = document.getElementById('edit_case_manager_modal');
        if (!this.editModal) return;

        this.editForm = this.editModal.querySelector('#edit-case-manager-form');

        // Open on pencil icon click (delegated)
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.edit-member-btn');
            if (!btn) return;
            e.preventDefault();
            this.openEditModal(
                btn.dataset.userId,
                btn.dataset.userName,
                btn.dataset.userEmail,
                btn.dataset.userLocked === '1',
                btn.dataset.userAp === '1'
            );
        });

        // Close buttons
        this.editModal.querySelector('.close_modal')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeEditModal();
        });
        document.getElementById('edit-member-cancel')?.addEventListener('click', () => {
            this.closeEditModal();
        });
        this.editModal.querySelector('.overlay')?.addEventListener('click', () => {
            this.closeEditModal();
        });

        // Unlock button
        document.getElementById('edit-member-unlock')?.addEventListener('click', () => {
            this.confirmUnlock();
        });

        this.editForm?.addEventListener('submit', (e) => this.handleEditSubmit(e));
    }

    openEditModal(userId, userName, userEmail, isLocked = false, isAp = false) {
        if (!this.editModal) return;

        document.getElementById('edit-member-user-id').value = userId || '';
        document.getElementById('edit-member-name').value    = userName  || '';
        document.getElementById('edit-member-email').value   = userEmail || '';

        const apCheckbox = document.getElementById('edit-member-accounts-payable');
        if (apCheckbox) apCheckbox.checked = isAp;

        const lockedWrap = document.getElementById('edit-member-locked-wrap');
        if (lockedWrap) lockedWrap.style.display = isLocked ? 'block' : 'none';

        this.fadeIn(this.editModal);
    }

    closeEditModal() {
        this.fadeOut(this.editModal);
    }

    async handleEditSubmit(e) {
        e.preventDefault();

        const saveBtn = this.editForm.querySelector('.save_button');
        saveBtn.disabled = true;
        saveBtn._originalText = saveBtn.textContent;
        saveBtn.innerHTML = '<span class="btn-spinner"></span>';

        const userId = document.getElementById('edit-member-user-id').value;
        const name   = document.getElementById('edit-member-name').value.trim();
        const email  = document.getElementById('edit-member-email').value.trim();
        const nonce  = this.editModal.querySelector('[name="edit_company_member_nonce"]')?.value || '';
        const isAp   = document.getElementById('edit-member-accounts-payable')?.checked ? '1' : '0';

        try {
            const params = new URLSearchParams({
                action:          'edit_company_member',
                user_id:         userId,
                name,
                email,
                accounts_payable: isAp,
                nonce,
            });

            const res  = await fetch(docuswiftPortal.ajax_url, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    params.toString(),
            });
            const data = await res.json();

            if (data.success) {
                this.updateMemberRow(userId, data.data);
                this.closeEditModal();
                this.toast(data.data.message || 'Case manager updated.', 'success');
            } else {
                this.toast(data.data?.message || 'Failed to update user.', 'error');
            }

        } catch (err) {
            console.error(err);
            this.toast('Network error. Please try again.', 'error');
        } finally {
            saveBtn.disabled  = false;
            saveBtn.textContent = saveBtn._originalText || 'Save Changes';
        }
    }

    async confirmUnlock() {
        const userId   = document.getElementById('edit-member-user-id').value;
        const nonce    = this.editModal.querySelector('[name="unlock_company_member_nonce"]')?.value || '';
        const unlockBtn = document.getElementById('edit-member-unlock');

        unlockBtn.disabled = true;
        unlockBtn._originalText = unlockBtn.textContent;
        unlockBtn.innerHTML = '<span class="btn-spinner btn-spinner--dark"></span>';

        try {
            const params = new URLSearchParams({
                action:  'unlock_company_member',
                user_id: userId,
                nonce,
            });

            const res  = await fetch(docuswiftPortal.ajax_url, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    params.toString(),
            });
            const data = await res.json();

            if (data.success) {
                // Hide the locked section and update the edit button attribute
                document.getElementById('edit-member-locked-wrap').style.display = 'none';
                const btn = this.tableBody?.querySelector(`.edit-member-btn[data-user-id="${userId}"]`);
                if (btn) btn.dataset.userLocked = '0';
                this.toast(data.data.message || 'Account unlocked.', 'success');
            } else {
                this.toast(data.data?.message || 'Failed to unlock account.', 'error');
                unlockBtn.disabled = false;
                unlockBtn.textContent = unlockBtn._originalText || 'Unlock Account';
            }

        } catch (err) {
            console.error(err);
            this.toast('Network error. Please try again.', 'error');
            unlockBtn.disabled = false;
            unlockBtn.textContent = unlockBtn._originalText || 'Unlock Account';
        }
    }

    updateMemberRow(userId, data) {
        const btn = this.tableBody?.querySelector(`.edit-member-btn[data-user-id="${userId}"]`);
        if (!btn) return;

        btn.dataset.userName  = data.name;
        btn.dataset.userEmail = data.email;
        if (data.is_ap !== undefined) btn.dataset.userAp = data.is_ap ? '1' : '0';

        const row = btn.closest('tr');
        if (!row) return;

        const nameDiv = row.querySelector('.name');
        if (nameDiv) {
            const usernameSpan = nameDiv.querySelector('.username');
            nameDiv.childNodes[0].textContent = data.name;
            if (usernameSpan) usernameSpan.textContent = '@' + data.email;
        }

        const emailCell = row.querySelector('td:nth-child(2) a');
        if (emailCell) {
            emailCell.href        = 'mailto:' + data.email;
            emailCell.textContent = data.email;
        }

        const deleteBtn = row.querySelector('.delete-member-btn');
        if (deleteBtn) deleteBtn.dataset.userName = data.name;
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    resetDeleteBtn(disabled = false) {
        const btn = document.getElementById('delete-member-confirm');
        if (!btn) return;
        btn.disabled = disabled;
        btn.textContent = 'Delete';
    }

    toast(message, type = 'success') {
        showToast(message, type);
    }

    fadeIn(el) {
        el.style.display = 'block';
        el.style.opacity = 0;
        el.style.transition = 'opacity 0.2s';
        requestAnimationFrame(() => { el.style.opacity = 1; });
    }

    fadeOut(el) {
        el.style.opacity = 0;
        el.style.transition = 'opacity 0.2s';
        setTimeout(() => { el.style.display = 'none'; }, 200);
    }
}